import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  TemplateRef
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { CompetitionsService } from '@usheru-hff/movie-detail/feature-competitions';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import { isLocalhost } from '@usheru-hff/sdk/utils';
import {
  BrandedSection,
  Channel,
  CustomMovie,
  getMovieGenres,
  getMovieTags,
  Movie,
  MovieBrandedLink,
  PageLink,
  TabType
} from '@usheru-hff/shared/domain';
import { VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { TemplatesService, ThemeService } from '@usheru-hff/shared/utils';
import { CookiesCheck, CookieSettingsService, CookiesSettingsType } from '@usheru-hff/shared/utils-cookies';
import { ImageService } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { NavigationService, PageLoaderService } from '@usheru-hff/shared/utils-navigation';
import { TitleService } from '@usheru-hff/shared/utils-title';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';
import { UplayerService } from '@usheru-hff/uplayer/domain';

@Component({
  selector: 'ush-branded-movie-template-base',
  template: '<p>branded base works</p>',
  styles: []
})
export class BrandedMovieTemplate implements OnInit, OnDestroy, AfterViewInit, CookiesCheck {
  SECTIONS = [
    'in-cinemas',
    'watch-at-home',
    'synopsis-and-cast',
    'social-updates',
    'contact',
    'gallery',
    'watch-trailer',
    'sign-up'
  ];
  template: 'vertical' | 'horizontal' | 'default' = 'vertical';

  @Input()
  customMovie: CustomMovie;

  @Input() synopsisTopAddon?: TemplateRef<any>;

  @Input() movieMenuExtraElements: TemplateRef<unknown>;

  channel: Channel;
  movie: Movie;
  loading: boolean = false;
  section: string = '';
  hasCompetitions = false;
  langSelectorInMenu: boolean;
  synopsisPoster: boolean;
  cookiesRequired: CookiesSettingsType[] = ['functionality_storage'];

  brandedPageLinks: PageLink[] = [];
  extraLinks: { key: string; url: string }[];

  hidenTabs: TabType[];
  customMovieLogo: string;

  backgroundColor: string;
  backgroundUrl: string;
  backgroundVideo: string;
  backgroundStyle;
  footerTransparentBackground: boolean;
  linearGradientColor: string;

  watchAtHomeIsAvailable: boolean;
  inCinemasIsAvailable: boolean;
  disableReviews: boolean;
  disableShowtimes: boolean;
  disableStreaming: boolean;

  demandItEnabled: boolean;

  hasOnlyOneCountryEnabled: boolean;

  @HostBinding('class.gallery-open') galleryOpen: boolean;
  scrolled: boolean;

  subscriptions: Subscription[] = [];

  responsiveOptions: any[] = [
    {
      breakpoint: '1024px',
      numVisible: 5
    },
    {
      breakpoint: '960px',
      numVisible: 4
    },
    {
      breakpoint: '768px',
      numVisible: 3
    },
    {
      breakpoint: '560px',
      numVisible: 1
    }
  ];
  // Templates refs
  brandedInCinemasAddon: TemplateRef<any>;
  brandedExtraSection: BrandedSection[];
  streamingPlatformsLoadedTracked: boolean;
  showtimesLoadedTracked: boolean;
  portraitPlaceholder: string;
  qParams: Params;
  isBrowser: boolean = false;

  usePlaceholderAssets = this.isLocalEnv && this.environment?.placeholderData?.assets;

  constructor(
    @Inject(ENVIRONMENT) public environment: Environment,
    @Inject(PLATFORM_ID) public platform,
    public channelService: ChannelService,
    public movieService: MovieService,
    public themeService: ThemeService,
    public titleService: TitleService,
    public videPlayerService: VideoFullscreenService,
    public metaTagService: MetatagsService,
    public imageService: ImageService,
    public route: ActivatedRoute,
    public router: Router,
    public location: Location,
    public translationsService: TranslationsService,
    public locationService: LocationService,
    public navigationService: NavigationService,
    public templatesService: TemplatesService,
    public trackingService: TrackingService,
    public competitionsService: CompetitionsService,
    public pageLoader: PageLoaderService,
    public cookiesService: CookieSettingsService,
    public sanitizer: DomSanitizer,
    public uplayerService: UplayerService,
    public cd: ChangeDetectorRef,
    public showtimeStreamingService: ShowtimeStreamingService,
    @Inject(DOCUMENT) public document: Document
  ) {
    this.isBrowser = isPlatformBrowser(this.platform);
    // updating booleans
    this.disableReviews = !this.environment?.customPageParams?.movieDetailReviews;
    this.disableShowtimes = this.channelService.channel.settings.disableShowtimes;
    this.disableStreaming = this.channelService.channel.settings.disableStreaming;
    this.demandItEnabled = this.channelService.channel.commsConsumerDemandit;

    // providing inCinemas addon if exists
    this.brandedInCinemasAddon = this.templatesService.get('brandedInCinemasAddon');
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(qParams => {
      this.qParams = qParams;

      // if there is a template property then assign it
      if (this.customMovie.template) this.template = this.customMovie.template;

      // if custom watch at home route found then set it up in the navigation service
      if (this.customMovie.watchAtHomeTag) {
        this.navigationService.addCustomWatchAtHomeRoute(this.customMovie.watchAtHomeTag);
      }

      if (this.customMovie.customMobileMenuStyle) {
        this.themeService.setBrandedMobileMenuStyle(this.customMovie.customMobileMenuStyle);
      }

      this.channel = this.channelService.channel;

      // if there is a linear background boolean use it
      if (this.customMovie.backgroundLinearGradient == false) {
        this.linearGradientColor = 'transparent';
      } else {
        this.linearGradientColor = this.backgroundColor;
      }

      // if there is a custom background set the body with this img or set the body with tile img
      if (this.customMovie.customPageBackground) {
        this.setBodyBackground(this.customMovie.customPageBackground);
      }

      // if videoBackground then set the backgroundVIdeo
      if (this.customMovie.customPageVideoBackground) {
        this.backgroundVideo = this.customMovie.customPageVideoBackground;
      }

      // if env footer transparent then change the footer to transparent
      if (this.customMovie.transparentFooter) {
        this.footerTransparentBackground = true;
      }

      // enabling menu lang selector
      if (this.customMovie.langSelectorInMenu) {
        this.langSelectorInMenu = true;
      }

      // check if there is only one country enabled
      if (this.locationService.getAvailableCountries().length == 1) {
        this.hasOnlyOneCountryEnabled = true;
      }

      // check if this movie should have a poster in the synopsis level
      this.customMovie.synopsisPoster && (this.synopsisPoster = true);

      this.hidenTabs = this.customMovie.hideTabs;

      this.brandedExtraSection = this.customMovie.extraContentSection;
      this.customMovieLogo = this.customMovie.customMovieLogo;
      this.portraitPlaceholder = this.imageService.portraitPlaceholder;

      if (this.brandedExtraSection) {
        this.addExtraSectionsToSectionList();
      }

      this.hideDefaultFooter();

      this.loading = true;

      this.loadMovie();
    });
  }
  loadMovie() {
    this.movieService
      .getMovie(
        this.customMovie.slug,
        this.translationsService.currentLanguage().code,
        this.locationService.selectedCountry.slug
      )
      .subscribe(
        movie => {
          this.initMovie(movie);
        },
        err => {
          console.error(err);
        }
      );
  }

  initMovie(movie: Movie) {
    this.loading = false;
    this.movie = movie;

    // if pageBackground was not and there is no background video set from env then we can place as a background the tile img of movie
    if (!this.backgroundUrl && !this.backgroundVideo) {
      this.setBodyBackground(movie.fullpageImageUrl || movie.tilePhotoUrl || movie.tilePhoto);
    }
    // checking if watch at home should be visible
    if (!this.disableStreaming && this.areStreamingAvailable(this.movie) && !this.isTabHiden('watch-at-home')) {
      this.watchAtHomeIsAvailable = true;
    }
    // checking if in cinemas shoudl be visible
    if (!this.disableShowtimes && this.areShowtimesAvailable(this.movie)) {
      this.inCinemasIsAvailable = true;
    }

    //this.movieStyle.background = `linear-gradient(90deg, ${this.customMovie.backgroundColor}, transparent, transparent), url(${this.movie.fullpageImageUrl})`;
    this.setMetatags(movie);

    this.provideAvailablePageLinksToMobileMenu();

    // select section from url if any
    this.initializeSection();

    this.setInitialTracking();

    // initialize player prices
    if (this.movie.hasPlayer) this.uplayerService.retrieveAvailablePlayerPrices(movie.id);

    // if trailer playing on opening
    if (this.customMovie.trailerAutoplay && !this.customMovie.trailerEmbed) this.playTrailer({ muted: true });

    // hide page loader
    this.pageLoader.hidePageLoader();
    this.cd.detectChanges();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      try {
        setTimeout(() => {
          if (this.isBrowser) this.scrollToSection(this.qParams.whereToWatch || this.qParams.section);
        }, 1000);
      } catch (e) {
        console.error(e);
      }
    }
  }

  getGallerySrcSets(src) {
    // Example: Fetch the srcset value from an API response
    const galleryOpenSrcset =
      this.imageService.computeUrl(src, { width: 360 }) +
      ' 250w, ' +
      this.imageService.computeUrl(src, { width: 450 }) +
      ' 450w, ' +
      this.imageService.computeUrl(src, { width: 900 }) +
      ' 900w, ' +
      this.imageService.computeUrl(src, { width: 1200 }) +
      ' 1200w, ' +
      this.imageService.computeUrl(src, { width: 1600 }) +
      ' 1600w, ' +
      this.imageService.computeUrl(src, { width: 2000 }) +
      ' 2000w';
    return this.sanitizer.bypassSecurityTrustResourceUrl(galleryOpenSrcset);
  }

  addExtraSectionsToSectionList() {
    this.brandedExtraSection.forEach(section => {
      this.SECTIONS.push(section.trName);
    });
  }

  private setInitialTracking() {
    this.trackingService.trackEvent({
      action: 'FilmPageView',
      properties: {
        category: 'MovieDetail',
        label: this.movie.title,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });
  }

  private setMetatags(movie: Movie): void {
    const metaTags: MetaTags = {
      url: this.location.path(),
      title: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.title`)
        ? `movie.${movie.friendlyUrl}.title`
        : `meta.page.movie-detail.title`,
      favicon: movie?.faviconImageUrl,
      description: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.description`)
        ? `movie.${movie.friendlyUrl}.description`
        : `meta.page.movie-detail.description`,
      video: movie.trTrailerLink
    };

    if (movie.tilePhotoUrl) {
      metaTags.image = this.imageService.computeUrl(movie.tilePhotoUrl, { width: 1280 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imageService.computeUrl(movie.portraitPhotoUrl, { height: 1000 });
    }

    //TODO cases with more directors?
    const director = movie?.movieCrew?.find(mc => mc.crewRole.name.toLowerCase() == 'director')?.star?.name;

    this.metaTagService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
  }

  gotoSection(link: PageLink): void {
    const section = link.name;
    if (link.name === 'competitions') {
      this.subscriptions.push(
        this.competitionsService.getCompetition(this.movie.id).subscribe(competition => {
          this.trackingService.trackEvent({
            action: 'SelectCompetition',
            properties: {
              label: `${competition.title} @ ${this.movie.title}`,
              category: 'Competitions',
              movie: this.movie.title,
              idMovie: this.movie.id,
              imdb: this.movie?.imdb,
              tagsMovie: getMovieTags(this.movie),
              genresMovie: getMovieGenres(this.movie),
              competitionTitle: competition.title,
              idCompetition: competition.id
            }
          });
        })
      );
    } else {
      this.trackingService.trackEvent({
        action: 'MovieMenu',
        properties: {
          category: 'MovieDetail',
          label: section,
          movie: this.movie.title,
          idMovie: this.movie.id,
          imdb: this.movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie)
        }
      });
    }

    if (link.link) {
      const params = this.route.snapshot.params;
      this.router.navigateByUrl(link.link, {
        replaceUrl: !params.section
      });

      return;
    }

    // if it's a watch-trailer url then play trailer straight
    if (!this.customMovie?.trailerComponentVisible && section == 'watch-trailer') {
      this.playTrailer();
    } else if (section == 'gallery') {
      const gallery = document.getElementById('gallery');
      if (gallery) {
        this.scrolled = false;
        this.scrollToSection(section);
        this.location.go(`/movie/${this.movie.friendlyUrl}`, 'section=gallery');
      } else {
        this.openGallery();
      }
    } else {
      this.scrolled = false;
      this.scrollToSection(section);
      this.location.go(`/movie/${this.movie.friendlyUrl}`, `section=${section}`);
      this.pageLoader.hidePageLoader();
    }
  }

  trackingLink(link: PageLink) {
    this.trackingService.trackEvent({
      action: 'MovieMenu',
      properties: {
        category: 'MovieDetail',
        label: link.name,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });
  }

  get fontPath() {
    return `assets/fonts/${this.customMovie.font}`;
  }

  get fontBoldPath() {
    return `assets/fonts/${this.customMovie.fontBold}`;
  }

  playTrailer(settings?: any): void {
    // check for cookies first
    if (!this.cookiesService.areCookiesConsentEnabled(this.cookiesRequired)) {
      this.cookiesService.getCookieConsentInput(this.cookiesRequired).then(enabled => {
        if (enabled) this.playTrailer(settings);
      });
    } else {
      if (this.movie) {
        this.videPlayerService.play({ content: this.movie, category: 'MovieDetail', videoSettings: settings });
      }
    }
  }

  linkIsAvailable(tabName: TabType): boolean {
    return this.brandedPageLinks.some(pageLink => pageLink.name == tabName);
  }

  openGallery(): void {
    this.galleryOpen = true;
  }

  setBodyBackground(url?: string) {
    this.backgroundUrl = url;

    if (url) {
      this.backgroundStyle = `linear-gradient(90deg, transparent, transparent, transparent), url(${url}),
        'background-size': 'cover',
        'background-attachment': 'fixed',
        'background-repeat': 'no-repeat',
        'background-position': 'right'`;
    }
  }

  hideDefaultFooter(): void {
    // if (document.getElementById('main-footer')) {
    //   document.getElementById('main-footer').style.display = 'none';
    // }
  }

  showDefaultFooter() {
    //   if (document.getElementById('main-footer')) {
    //     document.getElementById('main-footer').style.display = 'block';
    //   }
  }

  ngOnDestroy() {
    this.titleService.setFavicon();
    this.metaTagService.resetMetaTags();
    // clear transparent background from footer
    this.showDefaultFooter();
    // clear body background if any
    // this.removeBodyBackground();
    // setting is branded page to false on destroy
    this.themeService.setBrandedMovie(null);
    this.themeService.setBrandedPageLinks([]);
    this.themeService.setBrandedMobileMenuStyle(null);
    this.navigationService.removeCustomWatchAtHomeRoute();
    this.subscriptions.forEach(subs => subs.unsubscribe());
  }

  populatePageLinks() {
    const linksName: MovieBrandedLink[] = [];

    if (this.watchAtHomeIsAvailable) linksName.push('watch-at-home');
    if ((this.inCinemasIsAvailable || this.demandItEnabled) && !this.isTabHiden('in-cinemas'))
      linksName.push('in-cinemas');
    // synopsis will be always present
    if (!this.isTabHiden('synopsis-and-cast')) linksName.push('synopsis-and-cast');
    if (this.movie.movieImages && !this.isTabHiden('gallery')) linksName.push('gallery');
    if (this.movie.trTrailerLink && !this.isTabHiden('watch-trailer')) linksName.push('watch-trailer');
    if (this.demandItEnabled && !this.isTabHiden('sign-up')) linksName.push('sign-up');

    if (this.brandedExtraSection) {
      this.brandedExtraSection.forEach(section => {
        if (!this.isTabHiden(section.trName)) linksName.push(section.trName);
      });
    }

    this.sortBrandedMenuLinks(linksName, this.customMovie.linksOrder);
    let pageLinks: PageLink[] = [];
    for (const linkName of linksName) {
      pageLinks.push({
        name: linkName,
        isActive: false
      });
    }

    // Add extra menu entries
    if (this.customMovie?.extraTabs) {
      pageLinks = [
        ...pageLinks,
        ...this.customMovie?.extraTabs.map(t => ({
          name: t.key,
          link: t.url,
          isExternal: true
        }))
      ];
    }

    this.brandedPageLinks = pageLinks;

    this.subscriptions.push(
      this.competitionsService.getCompetition(this.movie.id).subscribe(competition => {
        this.hasCompetitions = !!competition;
        if (this.hasCompetitions) {
          pageLinks.push({
            name: 'competitions',
            link: `/competitions/${this.movie?.id}`,
            isActive: false
          });
        }
      })
    );
  }

  isTabHiden(tab: any): boolean {
    if (this.hidenTabs) {
      for (const hiddenTab of this.hidenTabs) {
        if (tab == hiddenTab) {
          return true;
        }
      }
    }
    return false;
  }

  areShowtimesAvailable(movie: Movie) {
    return this.showtimeStreamingService.determineInCinemasAvailability(movie);
  }

  areStreamingAvailable(movie: Movie) {
    return this.showtimeStreamingService.determineStreamingAvailability(movie);
  }

  provideAvailablePageLinksToMobileMenu(): void {
    // populate page links
    this.populatePageLinks();

    // telling to the theme service that the header will be for a branded page by providing properties of the movie like social-links.
    this.themeService.setBrandedMovie(this.movie);

    // telling to the theme service that the header will be for a branded page by providing brandedPageLinks
    this.themeService.setBrandedPageLinks(this.brandedPageLinks);

    if (this.customMovie.customMobileMenuStyle) {
      this.themeService.setBrandedMobileMenuStyle(this.customMovie.customMobileMenuStyle);
    }

    // subscribing to sectionSelected to see what section was selected from header mobile
    this.subscriptions.push(
      this.themeService.getSectionSelected().subscribe(section => {
        // apply change if different to empty
        const selected = this.brandedPageLinks.find(link => link.name === section);
        if (selected) this.gotoSection(selected);
      })
    );
  }

  initializeSection() {
    // Put watch-trailer at firts if it is a video-thumnail
    if (this.template === 'horizontal' && this.customMovie?.trailerComponentVisible) {
      this.brandedPageLinks.sort((a, b) => (a.name === 'watch-trailer' ? -1 : 1));
    }

    this.initializeSectionIfNotSetted();
  }

  initializeSectionIfNotSetted() {
    if (this.section) return;
    if (this.areShowtimesAvailable(this.movie) && !this.isTabHiden('in-cinemas')) {
      this.section = 'in-cinemas';
    } else if (this.areStreamingAvailable(this.movie) && !this.isTabHiden('watch-at-home')) {
      this.section = 'watch-at-home';
    } else if (!this.isTabHiden('synopsis-and-cast')) {
      this.section = 'synopsis-and-cast';
    }
  }

  scrollToSection(sectionId: string) {
    if (sectionId != 'all') {
      const section = document.getElementById(sectionId);

      if (section) {
        const offesetHeader = document.querySelector('.header-wrapper').clientHeight;
        const contentWrapper = document.querySelector('.content-wrapper');
        const offsetMargin = parseInt(window.getComputedStyle(contentWrapper).marginTop, 10);
        const offsetY = offesetHeader + offsetMargin;
        const top = section.getBoundingClientRect().top + window.pageYOffset - offsetY;
        window.scrollTo({ top, behavior: 'smooth' });
        this.scrolled = true;
      }
    }
    if (!sectionId) return;

    this.section = sectionId;
  }

  sortBrandedMenuLinks(links: MovieBrandedLink[], sorting: { link: MovieBrandedLink; position: number }[]) {
    if (sorting) {
      links.sort((a, b) => {
        let bFound: { link: MovieBrandedLink; position: number };
        let aFound: { link: MovieBrandedLink; position: number };
        aFound = sorting.find(elem => elem.link == a);
        bFound = sorting.find(elem => elem.link == b);

        // if both elements found then sort them by their position
        if (aFound && bFound) {
          return aFound.position - bFound.position;
        }
        if (aFound && !bFound) {
          return aFound.position - 500;
        }
        if (!aFound && bFound) {
          return 500 - bFound.position;
        }
        return 0;
      });
    }
  }

  /**
   * Method that will track the StreamingLoaded event if there are streams present.
   */
  onStreamingPlatformsLoaded() {
    if (!this.streamingPlatformsLoadedTracked) {
      this.streamingPlatformsLoadedTracked = true;
      this.trackingService.trackEvent({
        action: 'StreamingLoaded',
        properties: {
          category: 'MovieStreaming',
          label: `${this.movie.title}`,
          movie: this.movie.title,
          idMovie: this.movie.id,
          imdb: this.movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie)
        }
      });
    }
  }

  /**
   * Method that will track ShowtimesLoaded action if ther are performances present.
   */
  onShowtimesLoaded() {
    // only track if performances exists for this movie
    if (!this.showtimesLoadedTracked) {
      this.showtimesLoadedTracked = true;
      this.trackingService.trackEvent({
        action: 'ShowtimesLoaded',
        properties: {
          category: 'MovieShowtimes',
          label: `${this.movie.title}`,
          movie: this.movie.title,
          idMovie: this.movie.id,
          imdb: this.movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie)
        }
      });
    }
  }

  get isLocalEnv() {
    return this.environment.mode == 'develop' || isLocalhost(this.document);
  }
}
